import React, { FC, useState, useEffect, useMemo } from 'react';
import { Select, Space, Button, Typography, Divider } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDashboardFiltersStore } from '@/store/dashboardFilters';

const { Text } = Typography;
const { Option, OptGroup } = Select;

export interface DateFilterValue {
  preset: string;
  startDate: string | null;
  endDate: string | null;
  displayText: string;
}

interface DateRangeFilterProps {
  onFilterChange: (value: DateFilterValue) => void;
  loading?: boolean;
}

// Smart rules for time resolution based on date range
interface SmartResolutionRule {
  minHours: number;
  maxHours: number;
  availableResolutions: string[];
  defaultResolution: string;
  description: string;
}

const SMART_RESOLUTION_RULES: SmartResolutionRule[] = [
  {
    minHours: 0,
    maxHours: 24, // Up to 1 day
    availableResolutions: ['Minute', 'Hour'],
    defaultResolution: 'Minute',
    description: 'Daily view - Minute/Hour granularity for detailed analysis'
  },
  {
    minHours: 24,
    maxHours: 168, // 1 day to 1 week
    availableResolutions: ['Hour', 'Day'],
    defaultResolution: 'Hour',
    description: 'Weekly view - Hour/Day granularity for trend analysis'
  },
  {
    minHours: 168,
    maxHours: 720, // 1 week to 1 month
    availableResolutions: ['Day', 'Week'],
    defaultResolution: 'Day',
    description: 'Monthly view - Day/Week granularity for pattern recognition'
  },
  {
    minHours: 720,
    maxHours: 2160, // 1 month to 3 months
    availableResolutions: ['Week', 'Month'],
    defaultResolution: 'Week',
    description: 'Quarterly view - Week/Month granularity for long-term trends'
  },
  {
    minHours: 2160,
    maxHours: Infinity, // 3+ months
    availableResolutions: ['Month'],
    defaultResolution: 'Month',
    description: 'Long-term view - Month granularity for historical analysis'
  }
];

const DateRangeFilter: FC<DateRangeFilterProps> = ({ onFilterChange, loading = false }) => {
  // Use the global dashboard filters store
  const onPeriodSelected = useDashboardFiltersStore(state => state.onPeriodSelected);
  const setOnPeriodSelected = useDashboardFiltersStore(state => state.setOnPeriodSelected);
  const filterByTimePeriod = useDashboardFiltersStore(state => state.filterByTimePeriod);
  const setFilterByTimePeriod = useDashboardFiltersStore(state => state.setFilterByTimePeriod);
  const filterDates = useDashboardFiltersStore(state => state.filterDates);
  const setFilterDates = useDashboardFiltersStore(state => state.setFilterDates);

  // Map preset labels to their codes for the UI display
  const presetCodes: Record<string, string> = {
    'Past 1 Hour': '1h',
    'Past 6 Hours': '6h',
    'Past 12 Hours': '12h',
    'Past 1 Day': '1d',
    'Past 3 Days': '3d',
    'Past 7 Days': '7d',
    'Past 14 Days': '14d',
    'Past 30 Days': '30d',
    'Past 3 Months': '3mo',
    'Past 6 Months': '6mo',
    'Past 1 Year': '1y',
    'Custom Range': 'custom'
  };

  // Enhanced predefined date range presets with logical grouping
  const presetGroups = [
    {
      label: 'Real-time & Daily',
      presets: [
        { value: 'Past 1 Hour', label: 'Past 1 Hour', days: 0, hours: 1 },
        { value: 'Past 6 Hours', label: 'Past 6 Hours', days: 0, hours: 6 },
        { value: 'Past 12 Hours', label: 'Past 12 Hours', days: 0, hours: 12 },
        { value: 'Past 1 Day', label: 'Past 1 Day', days: 1, hours: 0 },
      ]
    },
    {
      label: 'Weekly & Bi-weekly',
      presets: [
        { value: 'Past 3 Days', label: 'Past 3 Days', days: 3, hours: 0 },
        { value: 'Past 7 Days', label: 'Past 7 Days', days: 7, hours: 0 },
        { value: 'Past 14 Days', label: 'Past 14 Days', days: 14, hours: 0 },
      ]
    },
    {
      label: 'Monthly & Quarterly',
      presets: [
        { value: 'Past 30 Days', label: 'Past 30 Days', days: 30, hours: 0 },
        { value: 'Past 3 Months', label: 'Past 3 Months', days: 90, hours: 0 },
        { value: 'Past 6 Months', label: 'Past 6 Months', days: 180, hours: 0 },
      ]
    },
    {
      label: 'Long-term',
      presets: [
        { value: 'Past 1 Year', label: 'Past 1 Year', days: 365, hours: 0 },
      ]
    }
  ];

  // All presets flattened for easy lookup
  const allPresets = presetGroups.flatMap(group => group.presets).concat([
    { value: 'Custom Range', label: 'Custom Range', days: 0, hours: 0 }
  ]);

  // All available resolution options
  const allResolutions = [
    { value: 'Minute', label: 'Minute' },
    { value: 'Hour', label: 'Hour' },
    { value: 'Day', label: 'Day' },
    { value: 'Week', label: 'Week' },
    { value: 'Month', label: 'Month' },
  ];

  // Smart resolution logic based on selected date range
  const getSmartResolutions = useMemo(() => {
    if (!filterDates || filterDates.length !== 2) {
      return {
        availableResolutions: allResolutions,
        defaultResolution: 'Hour',
        rule: null
      };
    }

    const startDate = filterDates[0];
    const endDate = filterDates[1];
    const diffInHours = endDate.diff(startDate, 'hour');

    // Find the appropriate rule
    const rule = SMART_RESOLUTION_RULES.find(
      rule => diffInHours >= rule.minHours && diffInHours < rule.maxHours
    ) || SMART_RESOLUTION_RULES[SMART_RESOLUTION_RULES.length - 1]; // Default to last rule

    const availableResolutions = allResolutions.filter(res => 
      rule.availableResolutions.includes(res.value)
    );

    return {
      availableResolutions,
      defaultResolution: rule.defaultResolution,
      rule
    };
  }, [filterDates]);

  // Auto-adjust resolution when date range changes
  useEffect(() => {
    const { defaultResolution, availableResolutions } = getSmartResolutions;
    
    // If current resolution is not available for the new date range, switch to default
    const isCurrentResolutionAvailable = availableResolutions.some(
      res => res.value === filterByTimePeriod
    );
    
    if (!isCurrentResolutionAvailable) {
      console.log(`DateRangeFilter - Auto-adjusting resolution from ${filterByTimePeriod} to ${defaultResolution}`);
      setFilterByTimePeriod(defaultResolution);
    }
  }, [filterDates, getSmartResolutions, filterByTimePeriod, setFilterByTimePeriod]);
  
  // Handle preset selection change
  const handlePresetChange = (value: string) => {
    console.log("DateRangeFilter - Smart preset selection changed to:", value);
    setOnPeriodSelected(value);
    
    if (value !== 'Custom Range') {
      // Find the selected preset
      const preset = allPresets.find(p => p.value === value);
      if (preset) {
        const endDate = dayjs();
        let startDate;
        
        if (preset.hours > 0) {
          startDate = dayjs().subtract(preset.hours, 'hour');
        } else {
          startDate = dayjs().subtract(preset.days, 'day');
        }
        
        // Update global filterDates state
        setFilterDates([startDate, endDate]);
        
        const filterValue = {
          preset: presetCodes[value] || value,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          displayText: value
        };
        
        console.log("DateRangeFilter - Smart filter applied:", {
          ...filterValue,
          diffInHours: endDate.diff(startDate, 'hour'),
          smartRule: getSmartResolutions.rule?.description
        });
        
        // Notify parent component about the filter change
        onFilterChange(filterValue);
      }
    }
  };

  // Handle time resolution change
  const handleResolutionChange = (value: string) => {
    console.log("DateRangeFilter - Resolution changed to:", value);
    setFilterByTimePeriod(value);
  };

  // Handle refresh button click
  const handleRefresh = () => {
    console.log("DateRangeFilter - Smart refresh triggered");
    // Retrigger the current filter with the current date range
    if (filterDates && filterDates.length === 2) {
      const filterValue = {
        preset: presetCodes[onPeriodSelected] || onPeriodSelected,
        startDate: filterDates[0].toISOString(),
        endDate: filterDates[1].toISOString(),
        displayText: onPeriodSelected
      };
      
      console.log("DateRangeFilter - Refreshing with smart filter:", {
        ...filterValue,
        activeRule: getSmartResolutions.rule?.description
      });
      onFilterChange(filterValue);
    }
  };

  // Update filter when component mounts or when global filter state changes
  useEffect(() => {
    if (filterDates && filterDates.length === 2) {
      const filterValue = {
        preset: presetCodes[onPeriodSelected] || onPeriodSelected,
        startDate: filterDates[0].toISOString(),
        endDate: filterDates[1].toISOString(),
        displayText: onPeriodSelected
      };
      
      console.log("DateRangeFilter - Smart sync with store:", {
        onPeriodSelected,
        filterDates: [
          filterDates[0].format('YYYY-MM-DD HH:mm'),
          filterDates[1].format('YYYY-MM-DD HH:mm')
        ],
        filterValue,
        smartRule: getSmartResolutions.rule?.description
      });
      
      onFilterChange(filterValue);
    }
  }, [filterDates, onPeriodSelected]);

  return (
    <div className="filter-container">
      <Space size="large">
        <div className="period-select">
          <Select
            value={onPeriodSelected}
            onChange={handlePresetChange}
            style={{ width: 240 }}
            disabled={loading}
            suffixIcon={<span className="select-suffix">▼</span>}
            placeholder="Select time period"
          >
            {presetGroups.map(group => (
              <OptGroup key={group.label} label={group.label}>
                {group.presets.map(preset => (
                  <Option key={preset.value} value={preset.value}>
                    {preset.label}
                  </Option>
                ))}
              </OptGroup>
            ))}
            <Divider style={{ margin: '4px 0' }} />
            <Option key="Custom Range" value="Custom Range">
              Custom Range
            </Option>
          </Select>
        </div>
        
        <div className="resolution-select">
          <Select
            value={filterByTimePeriod}
            onChange={handleResolutionChange}
            style={{ width: 140 }}
            disabled={loading}
            suffixIcon={<span className="select-suffix">▼</span>}
            placeholder="Resolution"
          >
            {getSmartResolutions.availableResolutions.map(res => (
              <Option key={res.value} value={res.value}>
                {res.label}
              </Option>
            ))}
          </Select>
        </div>
        
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
          title={getSmartResolutions.rule?.description || 'Refresh data'}
        >
          Refresh
        </Button>

        {getSmartResolutions.rule && (
          <div className="smart-filter-indicator">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Smart: {getSmartResolutions.rule.description.split(' - ')[0]}
            </Text>
          </div>
        )}
      </Space>
    </div>
  );
};

export default DateRangeFilter; 